import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import backk from "./images/backk.png";

const MotionDetection = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previousFrameRef = useRef(null);
  const [motionDetected, setMotionDetected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("No motion detected");

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      const token = response.access_token;
      sessionStorage.setItem("googleToken", token);

      try {
        const profileResponse = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${token}`
        );
        const userEmail = profileResponse.data.email;
        setEmail(userEmail);
        sessionStorage.setItem("userEmail", userEmail);
        setMessage("Logged in successfully!");
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setMessage("Failed to authenticate. Please try again.");
      }
    },
    onError: () => {
      console.error("Login Failed");
      setMessage("Google login failed. Please try again.");
    },
    scope: "https://www.googleapis.com/auth/drive.file",
  });

  useEffect(() => {
    login();
  }, [login]);

  const uploadToDrive = async (blob) => {
    const token = sessionStorage.getItem("googleToken");
    if (!token) {
      setMessage("Please log in with Google to upload.");
      return;
    }

    setIsUploading(true);
    try {
      const metadata = JSON.stringify({
        name: `motion_detected_${Date.now()}.webm`,
        mimeType: "video/webm",
      });

      const sessionRes = await axios.post(
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable",
        metadata,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const uploadUrl = sessionRes.headers.location;
      await axios.put(uploadUrl, blob, {
        headers: {
          "Content-Type": "video/webm",
        },
      });

      setMessage("Video uploaded successfully!");
    } catch (error) {
      console.error("Error uploading file:", error);
      setMessage("Failed to upload video.");
    } finally {
      setIsUploading(false);
    }
  };

  const startRecording = (videoStream) => {
    if (isRecording) return;

    setIsRecording(true);
    const mediaRecorder = new MediaRecorder(videoStream);
    const chunks = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      if (chunks.length > 0) {
        const blob = new Blob(chunks, { type: "video/webm" });
        await uploadToDrive(blob);
      }
      setIsRecording(false);
    };

    mediaRecorder.start();
    setTimeout(() => {
      if (mediaRecorder.state === "recording") {
        mediaRecorder.stop();
      }
    }, 5000); // Record for 5 seconds
  };

  const detectMotion = (prevFrame, currFrame) => {
    const motionThreshold = 50000; // Higher threshold for meaningful motion
    const pixelThreshold = 100; // Minimum number of changed pixels
    let totalDifference = 0;
    let pixelCount = 0;

    for (let i = 0; i < prevFrame.data.length; i += 4) {
      const diff =
        Math.abs(prevFrame.data[i] - currFrame.data[i]) +
        Math.abs(prevFrame.data[i + 1] - currFrame.data[i + 1]) +
        Math.abs(prevFrame.data[i + 2] - currFrame.data[i + 2]);

      if (diff > 30) {
        totalDifference += diff;
        pixelCount++;
      }
    }

    return totalDifference > motionThreshold && pixelCount > pixelThreshold;
  };

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240 },
        });
        video.srcObject = stream;
        video.play();

        const context = canvas.getContext("2d");
        canvas.width = 320;
        canvas.height = 240;

        const detectInterval = setInterval(() => {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const currentFrame = context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );

          if (previousFrameRef.current) {
            const motion = detectMotion(previousFrameRef.current, currentFrame);
            if (motion && !isRecording) {
              setMotionDetected(true);
              setMessage("Motion detected! Recording...");
              startRecording(stream);
            } else if (!motion && !motionDetected) {
              setMessage("No motion detected");
            }
          }

          previousFrameRef.current = currentFrame;
        }, 1000); // Lowered frequency to reduce flickering

        return () => clearInterval(detectInterval);
      } catch (error) {
        console.error("Error accessing camera:", error);
      }
    };

    startCamera();

    return () => {
      if (video.srcObject) {
        video.srcObject.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isRecording]);

  const handleBackClick = () => {
    setMessage("Going back...");
  };

  return (
    <div
      style={{
        background: "linear-gradient(to bottom, #fff7ad, #ffa9f9)",
        minHeight: "100vh",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <h1 style={{ color: "pink", fontSize: "36px" }}>Motion Detection</h1>
      <p>{email ? `Logged in as: ${email}` : "Authenticating..."}</p>
      <div
        style={{
          position: "relative",
          width: "320px",
          height: "240px",
          margin: "0 auto",
        }}
      >
        <video
          ref={videoRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          autoPlay
          muted
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        {motionDetected && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 0, 0, 0.3)",
            }}
          />
        )}
      </div>
      {isUploading && <p>Uploading video...</p>}
      {message && <p>{message}</p>}
      <button
        type="button"
        onClick={handleBackClick}
        style={{
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <img src={backk} width="30" height="40" alt="Back" />
      </button>
    </div>
  );
};

export default MotionDetection;
